import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, CircularProgress, Avatar, Stepper, Step, StepLabel,
  Divider, Tooltip, IconButton,
} from '@mui/material';
import {
  TransferWithinAStation, Add, CheckCircle, Cancel, Edit,
  ArrowForward, PlayArrow, Publish,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_STEPS = ['draft', 'submitted', 'approved', 'applied'];
const STATUS_COLOR: Record<string, string> = {
  draft: '#78909c', submitted: '#1565c0', approved: '#2e7d32',
  rejected: '#c62828', applied: '#4527a0', cancelled: '#78909c',
};

const CHANGE_TYPES: Record<string, { label: string; emoji: string; color: string }> = {
  promotion:            { label: 'Promotion',             emoji: '📈', color: '#2e7d32' },
  demotion:             { label: 'Demotion',              emoji: '📉', color: '#c62828' },
  lateral_move:         { label: 'Lateral Move',          emoji: '↔️', color: '#1565c0' },
  title_change:         { label: 'Title Change',          emoji: '🏷️', color: '#4527a0' },
  department_transfer:  { label: 'Dept Transfer',         emoji: '🏢', color: '#e65100' },
  reporting_line:       { label: 'Reporting Line Change', emoji: '🔗', color: '#00838f' },
  grade_change:         { label: 'Grade Change',          emoji: '⭐', color: '#f57f17' },
  location_change:      { label: 'Location Change',       emoji: '📍', color: '#880e4f' },
};

interface OrgChange {
  id: string; employeeId: string; requestedById: string;
  employee?: { firstName: string; lastName: string; department?: { name: string } };
  requestedBy?: { firstName: string; lastName: string };
  approver?: { firstName: string; lastName: string };
  changeType: string; status: string; effectiveDate: string; reason: string;
  fromTitle?: string; fromDepartment?: string; fromManager?: string; fromGrade?: string; fromLocation?: string;
  toTitle?: string; toDepartment?: string; toDepartmentId?: string; toManager?: string; toManagerId?: string;
  toGrade?: string; toLocation?: string;
  fromSalary?: number; toSalary?: number; currency?: string;
  approverNote?: string; hrNotes?: string; decidedAt?: string; appliedAt?: string;
  createdAt: string;
}

interface Stats { pending: string; approved_not_applied: string; applied_30d: string; total: string; byType: { changeType: string; count: string }[] }

const emptyForm = {
  employeeId: '', changeType: 'promotion', effectiveDate: '',
  reason: '', toTitle: '', toDepartmentId: '', toManagerId: '',
  toGrade: '', toLocation: '', fromSalary: '', toSalary: '', currency: 'HKD',
};

function empName(e?: { firstName: string; lastName: string }) {
  return e ? `${e.firstName} ${e.lastName}` : '—';
}

function ChangeChip({ type }: { type: string }) {
  const info = CHANGE_TYPES[type] ?? { label: type, emoji: '📋', color: '#666' };
  return (
    <Chip size="small" label={`${info.emoji} ${info.label}`}
      sx={{ bgcolor: info.color + '15', color: info.color, fontWeight: 600, fontSize: '0.7rem' }} />
  );
}

function BeforeAfter({ from, to, label }: { from?: string; to?: string; label: string }) {
  if (!from && !to) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>{label}</Typography>
      <Typography variant="body2" sx={{ textDecoration: to ? 'line-through' : undefined, color: 'text.secondary' }}>{from || '—'}</Typography>
      {to && <><ArrowForward fontSize="small" sx={{ color: '#2e7d32', fontSize: '0.9rem' }} /><Typography variant="body2" fontWeight={600} color="success.main">{to}</Typography></>}
    </Box>
  );
}

export default function OrgChanges() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');
  const isManager = (user as any)?.role === 'manager';
  const canCreate = isHR || isManager;

  const [tab, setTab] = useState(0);
  const [items, setItems] = useState<OrgChange[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [detailItem, setDetailItem] = useState<OrgChange | null>(null);
  const [actionDialog, setActionDialog] = useState<{ item: OrgChange; type: 'approve' | 'reject' } | null>(null);
  const [actionNote, setActionNote] = useState('');

  const STATUS_TABS = isHR
    ? ['All', 'Pending Review', 'Approved', 'Applied', 'Rejected/Cancelled']
    : ['My Requests'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const statusMap: Record<number, string | undefined> = {
        1: 'submitted', 2: 'approved', 3: 'applied',
      };
      const statusParam = isHR ? statusMap[tab] : undefined;
      const params = new URLSearchParams();
      if (statusParam) params.set('status', statusParam);

      const calls: Promise<any>[] = [
        apiFetch(`/api/org-changes?${params}`),
        apiFetch('/api/employees?limit=300'),
        apiFetch('/api/departments'),
      ];
      if (isHR) calls.push(apiFetch('/api/org-changes/stats'));

      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') {
        let data = results[0].value;
        if (isHR && tab === 4) data = data.filter((r: OrgChange) => ['rejected', 'cancelled'].includes(r.status));
        setItems(Array.isArray(data) ? data : []);
      }
      if (results[1].status === 'fulfilled') {
        const empData = results[1].value;
        setEmployees(Array.isArray(empData) ? empData : (empData.data ?? []));
      }
      if (results[2].status === 'fulfilled') setDepartments(results[2].value ?? []);
      if (results[3] && results[3].status === 'fulfilled') setStats((results[3] as any).value);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [tab, isHR]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setFormData({ ...emptyForm }); setFormOpen(true); };

  const openEdit = (item: OrgChange) => {
    setEditId(item.id);
    setFormData({
      employeeId: item.employeeId, changeType: item.changeType,
      effectiveDate: item.effectiveDate, reason: item.reason,
      toTitle: item.toTitle || '', toDepartmentId: item.toDepartmentId || '',
      toManagerId: item.toManagerId || '', toGrade: item.toGrade || '',
      toLocation: item.toLocation || '',
      fromSalary: item.fromSalary?.toString() || '',
      toSalary: item.toSalary?.toString() || '',
      currency: item.currency || 'HKD',
    });
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        fromSalary: formData.fromSalary ? Number(formData.fromSalary) : undefined,
        toSalary: formData.toSalary ? Number(formData.toSalary) : undefined,
      };
      if (editId) {
        await apiFetch(`/api/org-changes/${editId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await apiFetch('/api/org-changes', { method: 'POST', body: JSON.stringify(payload) });
      }
      setFormOpen(false);
      load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const submit = async (id: string) => {
    await apiFetch(`/api/org-changes/${id}/submit`, { method: 'PATCH' });
    load();
  };

  const handleAction = async () => {
    if (!actionDialog) return;
    const { item, type } = actionDialog;
    try {
      await apiFetch(`/api/org-changes/${item.id}/${type}`, {
        method: 'PATCH', body: JSON.stringify({ note: actionNote }),
      });
      setActionDialog(null); setActionNote('');
      load();
    } catch (e: any) { setError(e.message); }
  };

  const applyChange = async (id: string) => {
    await apiFetch(`/api/org-changes/${id}/apply`, { method: 'PATCH' });
    load();
  };

  const cancelReq = async (id: string) => {
    await apiFetch(`/api/org-changes/${id}/cancel`, { method: 'PATCH' });
    load();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#e65100', width: 48, height: 48 }}><TransferWithinAStation /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Org Change Requests</Typography>
            <Typography variant="body2" color="text.secondary">
              Promotions, transfers, title & reporting line changes
            </Typography>
          </Box>
        </Box>
        {canCreate && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}
            sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>
            New Request
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats row (HR) */}
      {isHR && stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Pending Review', value: parseInt(stats.pending), color: '#1565c0' },
            { label: 'Approved (not applied)', value: parseInt(stats.approved_not_applied), color: '#f57f17' },
            { label: 'Applied (last 30d)', value: parseInt(stats.applied_30d), color: '#2e7d32' },
            { label: 'Total', value: parseInt(stats.total), color: '#4527a0' },
          ].map(s => (
            <Grid size={{ xs: 6, sm: 3 }} key={s.label}>
              <Paper sx={{ p: 2, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {STATUS_TABS.map((t, i) => <Tab key={i} label={t} />)}
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Change Type</TableCell>
                <TableCell>Effective Date</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Raised By</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No requests found
                  </TableCell>
                </TableRow>
              )}
              {items.map(item => (
                <TableRow key={item.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailItem(item)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{empName(item.employee)}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.employee?.department?.name}</Typography>
                  </TableCell>
                  <TableCell><ChangeChip type={item.changeType} /></TableCell>
                  <TableCell><Typography variant="body2">{item.effectiveDate}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {item.fromTitle && item.toTitle ? `${item.fromTitle} → ${item.toTitle}` :
                       item.fromDepartment && item.toDepartment ? `${item.fromDepartment} → ${item.toDepartment}` :
                       item.reason?.slice(0, 50)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={item.status}
                      sx={{ bgcolor: STATUS_COLOR[item.status] + '20', color: STATUS_COLOR[item.status], fontWeight: 600 }} />
                  </TableCell>
                  <TableCell><Typography variant="caption">{empName(item.requestedBy)}</Typography></TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {item.status === 'draft' && (
                        <>
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(item)}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Submit for approval"><IconButton size="small" color="primary" onClick={() => submit(item.id)}><Publish fontSize="small" /></IconButton></Tooltip>
                        </>
                      )}
                      {item.status === 'submitted' && isHR && (
                        <>
                          <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => { setActionDialog({ item, type: 'approve' }); setActionNote(''); }}><CheckCircle fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => { setActionDialog({ item, type: 'reject' }); setActionNote(''); }}><Cancel fontSize="small" /></IconButton></Tooltip>
                        </>
                      )}
                      {item.status === 'approved' && isHR && (
                        <Tooltip title="Apply to employee record">
                          <IconButton size="small" color="success" onClick={() => applyChange(item.id)}><PlayArrow fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                      {['draft', 'submitted'].includes(item.status) && (
                        <Tooltip title="Cancel"><IconButton size="small" color="error" onClick={() => cancelReq(item.id)}><Cancel fontSize="small" /></IconButton></Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detailItem} onClose={() => setDetailItem(null)} maxWidth="sm" fullWidth>
        {detailItem && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ChangeChip type={detailItem.changeType} />
                <Typography variant="subtitle1" fontWeight={700}>{empName(detailItem.employee)}</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Stepper activeStep={Math.max(0, STATUS_STEPS.indexOf(detailItem.status))} sx={{ mb: 2 }}>
                {['Draft', 'Submitted', 'Approved', 'Applied'].map(s => (
                  <Step key={s}><StepLabel>{s}</StepLabel></Step>
                ))}
              </Stepper>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Effective: {detailItem.effectiveDate}</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>{detailItem.reason}</Typography>
              <Divider sx={{ mb: 1 }}><Typography variant="caption">Changes</Typography></Divider>
              <BeforeAfter from={detailItem.fromTitle} to={detailItem.toTitle} label="Title" />
              <BeforeAfter from={detailItem.fromDepartment} to={detailItem.toDepartment} label="Department" />
              <BeforeAfter from={detailItem.fromManager} to={detailItem.toManager} label="Manager" />
              <BeforeAfter from={detailItem.fromGrade} to={detailItem.toGrade} label="Grade" />
              <BeforeAfter from={detailItem.fromLocation} to={detailItem.toLocation} label="Location" />
              {(detailItem.fromSalary || detailItem.toSalary) && (
                <BeforeAfter
                  from={detailItem.fromSalary ? `${detailItem.currency} ${detailItem.fromSalary.toLocaleString()}` : undefined}
                  to={detailItem.toSalary ? `${detailItem.currency} ${detailItem.toSalary.toLocaleString()}` : undefined}
                  label="Salary"
                />
              )}
              {detailItem.approverNote && (
                <Alert severity={detailItem.status === 'rejected' ? 'error' : 'success'} sx={{ mt: 2 }}>
                  <strong>HR Note:</strong> {detailItem.approverNote}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailItem(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create / Edit Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit Request' : 'New Org Change Request'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField select fullWidth label="Employee" value={formData.employeeId}
            onChange={e => setFormData(p => ({ ...p, employeeId: e.target.value }))} required disabled={!!editId}>
            {employees.map(e => <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select fullWidth label="Change Type" value={formData.changeType}
              onChange={e => setFormData(p => ({ ...p, changeType: e.target.value }))}>
              {Object.entries(CHANGE_TYPES).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth label="Effective Date" type="date" InputLabelProps={{ shrink: true }}
              value={formData.effectiveDate} onChange={e => setFormData(p => ({ ...p, effectiveDate: e.target.value }))} required />
          </Box>
          <TextField fullWidth label="Reason / Justification" multiline rows={2}
            value={formData.reason} onChange={e => setFormData(p => ({ ...p, reason: e.target.value }))} required />
          <Divider><Typography variant="caption" color="text.secondary">Proposed Changes</Typography></Divider>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="New Title" value={formData.toTitle}
              onChange={e => setFormData(p => ({ ...p, toTitle: e.target.value }))} />
            <TextField fullWidth label="New Grade / Band" value={formData.toGrade}
              onChange={e => setFormData(p => ({ ...p, toGrade: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select fullWidth label="New Department" value={formData.toDepartmentId}
              onChange={e => setFormData(p => ({ ...p, toDepartmentId: e.target.value }))}>
              <MenuItem value="">— No change —</MenuItem>
              {departments.map((d: any) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="New Manager" value={formData.toManagerId}
              onChange={e => setFormData(p => ({ ...p, toManagerId: e.target.value }))}>
              <MenuItem value="">— No change —</MenuItem>
              {employees.map(e => <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName}</MenuItem>)}
            </TextField>
          </Box>
          <TextField fullWidth label="New Location" value={formData.toLocation}
            onChange={e => setFormData(p => ({ ...p, toLocation: e.target.value }))} />
          <Divider><Typography variant="caption" color="text.secondary">Salary (optional)</Typography></Divider>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Current Salary" type="number" value={formData.fromSalary}
              onChange={e => setFormData(p => ({ ...p, fromSalary: e.target.value }))} />
            <TextField fullWidth label="Proposed Salary" type="number" value={formData.toSalary}
              onChange={e => setFormData(p => ({ ...p, toSalary: e.target.value }))} />
            <TextField select label="Currency" value={formData.currency}
              onChange={e => setFormData(p => ({ ...p, currency: e.target.value }))} sx={{ minWidth: 90 }}>
              {['HKD', 'USD', 'SGD', 'EUR'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !formData.employeeId || !formData.effectiveDate || !formData.reason}
            sx={{ bgcolor: '#e65100' }}>
            {saving ? 'Saving…' : editId ? 'Update' : 'Save Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve/Reject Dialog */}
      <Dialog open={!!actionDialog} onClose={() => setActionDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{actionDialog?.type === 'approve' ? '✅ Approve Change' : '❌ Reject Change'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {actionDialog && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>{empName(actionDialog.item.employee)}</strong> — <ChangeChip type={actionDialog.item.changeType} />
            </Typography>
          )}
          <TextField fullWidth label={actionDialog?.type === 'reject' ? 'Reason (required)' : 'Note (optional)'}
            value={actionNote} onChange={e => setActionNote(e.target.value)} multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleAction}
            disabled={actionDialog?.type === 'reject' && !actionNote}
            color={actionDialog?.type === 'approve' ? 'success' : 'error'}>
            {actionDialog?.type === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
